/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Configurable Command Intelligence drawer: chat thread, optional intent router,
 * profile switcher, and render-prop context bar. Domain copy and agents come from
 * commandIntelligenceConfig supplied by the consuming app.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  getAiLimitUsageSummary,
  resolveAiAccessDeniedMessage,
  resolveChatApiErrorMessage,
  submitFeedback,
} from './chat/chatApi';
import { useCommandIntelligenceStreamingGeneration } from './chat/useCommandIntelligenceStreamingGeneration';
import {
  countUserPrompts,
  getLatestAssistantUsage,
  getLatestCreditLimitWarnings,
  isSessionContextLimitReached,
  isSessionPromptLimitReached,
  COMMAND_INTELLIGENCE_MAX_INPUT_CONTEXT_TOKENS,
  COMMAND_INTELLIGENCE_MAX_USER_PROMPTS,
} from './chat/chatSessionBudget';
import MessageList from './chat/MessageList.jsx';
import CommandIntelligenceAccessDenied from './CommandIntelligenceAccessDenied.jsx';
import CommandIntelligenceCreditsBar from './CommandIntelligenceCreditsBar.jsx';
import { getCommandIntelligenceDrawerContentStyles } from './CommandIntelligenceDrawerContent.styles';
import CommandIntelligencePromptInput from './CommandIntelligencePromptInput.jsx';
import CommandIntelligenceWelcomeHero from './CommandIntelligenceWelcomeHero.jsx';
import IntentProgressCard from './IntentProgressCard.jsx';
import {
  buildAmbientAttachedContext,
  getRecentApiCalls,
  hasNonEmptyAmbientContext,
  mergeAttachedContextWithAmbient,
  useAmbientCommandIntelligenceContext,
} from './ambient-context';
import { DOCK_PICKER_ANCHORS, DRAWER_LAYOUT_MODES, isVerticalDock } from './drawerLayout';
import { EntityContext } from '../../util/entity-provider/context';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import CompareArrows from '@mui/icons-material/CompareArrows';
import Layers from '@mui/icons-material/Layers';
import MoreVert from '@mui/icons-material/MoreVert';
import RestartAlt from '@mui/icons-material/RestartAlt';
import TouchApp from '@mui/icons-material/TouchApp';
import ViewSidebar from '@mui/icons-material/ViewSidebar';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

const makeId = prefix => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

const DOCK_ANCHOR_ICONS = {
  left: ArrowBack,
  right: ArrowForward,
  top: ArrowUpward,
  bottom: ArrowDownward,
};

const DOCK_ANCHOR_LABEL_IDS = {
  left: 'command_intelligence_dock_left',
  right: 'command_intelligence_dock_right',
  top: 'command_intelligence_dock_top',
  bottom: 'command_intelligence_dock_bottom',
};

const LAYOUT_MODE_ICONS = {
  overlap: Layers,
  push: ViewSidebar,
};

const LAYOUT_MODE_LABEL_IDS = {
  overlap: 'command_intelligence_panel_layout_overlap',
  push: 'command_intelligence_panel_layout_push',
};

const CommandIntelligenceDrawerContent = ({
  session,
  onClose,
  onAttachItems,
  onDetachItem,
  onClearAttached,
  commandIntelligenceConfig = {},
  renderContextBar,
  dockAnchor = 'right',
  onDockAnchorChange,
  dockPickerEnabled = false,
  layoutMode = 'overlap',
  onLayoutModeChange,
  layoutModePickerEnabled = false,
  autoHideOnClickOutside = false,
  onAutoHideOnClickOutsideChange,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isHorizontalDock = !isMobile && !isVerticalDock(dockAnchor);
  const styles = useMemo(
    () => getCommandIntelligenceDrawerContentStyles(theme, { isHorizontalDock }),
    [isHorizontalDock, theme]
  );
  const entityContext = useContext(EntityContext);
  const contextEntityRef = entityContext?.entityRef ?? '';
  const entityRef = commandIntelligenceConfig.entityRef ?? contextEntityRef ?? '';

  const availableItems = session?.availableItems ?? [];
  const profiles = commandIntelligenceConfig.profiles ?? [];
  const defaultProfileId = profiles[0]?.id ?? 'default';

  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [aiCreditsSummary, setAiCreditsSummary] = useState(null);
  const [aiCreditsStatus, setAiCreditsStatus] = useState('idle');
  const [aiAccessDeniedMessage, setAiAccessDeniedMessage] = useState(null);
  const [intentStatus, setIntentStatus] = useState('idle');
  const [intentEnvelope, setIntentEnvelope] = useState(null);
  const [intentCollapsed, setIntentCollapsed] = useState(false);
  const [assistantProfile, setAssistantProfile] = useState(defaultProfileId);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState(null);
  const overflowOpen = Boolean(overflowAnchorEl);

  const profileIdsKey = profiles
    .map(profile => profile?.id)
    .filter(Boolean)
    .join('|');

  useEffect(() => {
    const profileIds = profileIdsKey.split('|').filter(Boolean);

    setAssistantProfile(prev => (profileIds.includes(prev) ? prev : defaultProfileId));
  }, [defaultProfileId, profileIdsKey]);

  const intentAbortRef = useRef(null);
  const prevIsSendingRef = useRef(false);
  const channelRefRef = useRef(null);
  const mountedRef = useRef(true);

  const { mode, attachedItems, listFilters } = session;
  const ambient = useAmbientCommandIntelligenceContext();
  const ambientAttachedContext = useMemo(() => {
    if (!ambient?.enabled) {
      return null;
    }

    return buildAmbientAttachedContext({
      appRef: ambient?.appRef,
      routeContext: ambient?.routeContext,
      recentApiCalls: getRecentApiCalls(),
    });
  }, [ambient?.appRef, ambient?.enabled, ambient?.routeContext]);

  const preset = useMemo(
    () => commandIntelligenceConfig.getPreset?.(mode, assistantProfile) ?? {},
    [commandIntelligenceConfig, mode, assistantProfile]
  );
  const attachedCount = attachedItems?.length ?? 0;
  const hasContext = attachedCount > 0 || hasNonEmptyAmbientContext(ambientAttachedContext);
  const welcomeSubheadId = hasContext
    ? preset?.readySubheadId ?? 'command_intelligence_welcome_subhead'
    : preset?.subheadId;

  const intentContextSnapshot = useMemo(() => {
    if (typeof commandIntelligenceConfig.buildIntentContextSnapshot !== 'function') {
      return null;
    }

    return commandIntelligenceConfig.buildIntentContextSnapshot({ mode, attachedItems, session });
  }, [commandIntelligenceConfig, mode, attachedItems, session]);

  const attachedKey = useMemo(() => {
    if (typeof commandIntelligenceConfig.getAttachedKey === 'function') {
      return commandIntelligenceConfig.getAttachedKey(attachedItems);
    }

    return attachedItems
      .map(item => item?.crn ?? item?.ref ?? item?.orderRef ?? item?.id ?? '')
      .filter(Boolean)
      .join('|');
  }, [attachedItems, commandIntelligenceConfig]);

  const buildAttachedContextPayload = useCallback(() => {
    const overlayContext =
      commandIntelligenceConfig.buildAttachedContext?.({
        mode,
        attachedItems,
        listFilters,
        session,
      }) ?? {};

    if (!ambient?.enabled) {
      overlayContext.viewContext = {
        ...(overlayContext.viewContext ?? {}),
        route: window.location.pathname,
      };

      return overlayContext;
    }

    const snapshot = ambient?.getSnapshot?.();
    const ambientContext = buildAmbientAttachedContext({
      appRef: snapshot?.appRef,
      routeContext: snapshot?.routeContext,
      recentApiCalls: snapshot?.recentApiCalls ?? [],
    });

    return mergeAttachedContextWithAmbient(overlayContext, ambientContext);
  }, [ambient, commandIntelligenceConfig, mode, attachedItems, listFilters, session]);

  const wrapUserMessage = useCallback(
    text => commandIntelligenceConfig.buildUserMessage?.(text, { mode, attachedItems, session }) ?? text,
    [commandIntelligenceConfig, mode, attachedItems, session]
  );

  const { abortRef, streamingIdRef, handleStopGeneration, sendFirstMessage, sendFollowUpMessage } =
    useCommandIntelligenceStreamingGeneration({
      entityRef,
      intl,
      preset,
      mountedRef,
      channelRefRef,
      makeId,
      setMessages,
      setIsSending,
      isSending,
      wrapUserMessage,
      buildAttachedContextPayload,
    });

  const drawerTitleId = commandIntelligenceConfig.drawerTitleStringId ?? 'command_intelligence_drawer_title';

  const loadAiCreditsSummary = useCallback(async () => {
    if (!entityRef) {
      setAiCreditsSummary(null);
      setAiCreditsStatus('idle');
      setAiAccessDeniedMessage(null);

      return;
    }

    setAiCreditsStatus('loading');

    try {
      const data = await getAiLimitUsageSummary(entityRef);

      if (!mountedRef.current) {
        return;
      }

      if (data === null) {
        const errorMessage = await resolveChatApiErrorMessage(null, intl);

        setAiCreditsSummary(null);
        setAiAccessDeniedMessage(errorMessage);
        setAiCreditsStatus('forbidden');

        return;
      }

      if (data === undefined || typeof data !== 'object') {
        setAiCreditsSummary(null);
        setAiAccessDeniedMessage(null);
        setAiCreditsStatus('error');

        return;
      }

      setAiCreditsSummary(data);
      setAiCreditsStatus('ready');
      setAiAccessDeniedMessage(null);
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }

      const deniedMessage = await resolveAiAccessDeniedMessage(error, intl, { treat401AsAccessDenied: true });

      setAiCreditsSummary(null);

      if (deniedMessage !== null) {
        setAiAccessDeniedMessage(deniedMessage);
        setAiCreditsStatus('forbidden');
      } else {
        setAiAccessDeniedMessage(null);
        setAiCreditsStatus('error');
      }
    }
  }, [entityRef, intl]);

  useEffect(() => {
    void loadAiCreditsSummary();
  }, [loadAiCreditsSummary]);

  useEffect(() => {
    if (prevIsSendingRef.current && !isSending && entityRef && !aiAccessDeniedMessage) {
      void loadAiCreditsSummary();
    }

    prevIsSendingRef.current = isSending;
  }, [entityRef, isSending, loadAiCreditsSummary, aiAccessDeniedMessage]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
      intentAbortRef.current?.abort();
      intentAbortRef.current = null;
    };
  }, [abortRef]);

  useEffect(() => {
    setMessages([]);
    channelRefRef.current = null;
    streamingIdRef.current = null;
    setIsSending(false);
    setComposerText('');
    setIntentStatus('idle');
    setIntentEnvelope(null);
    setIntentCollapsed(false);
    abortRef.current?.abort();
    abortRef.current = null;
    intentAbortRef.current?.abort();
    intentAbortRef.current = null;
  }, [mode, attachedKey, assistantProfile, abortRef, streamingIdRef]);

  useEffect(() => {
    const runIntentRouter = commandIntelligenceConfig.runIntentRouter;

    if (!runIntentRouter || !entityRef || !hasContext || messages.length > 0) {
      return undefined;
    }

    const controller = new AbortController();

    intentAbortRef.current?.abort();
    intentAbortRef.current = controller;
    setIntentStatus('loading');
    setIntentEnvelope(null);
    setIntentCollapsed(false);

    runIntentRouter({
      entityRef,
      mode,
      contextSnapshot: intentContextSnapshot,
      signal: controller.signal,
      assistantProfile,
    })
      .then(result => {
        if (!mountedRef.current || controller.signal.aborted) {
          return;
        }

        if (result.ok) {
          setIntentEnvelope(result.envelope);
          setIntentStatus('ready');
          const collapseTimer = setTimeout(() => {
            if (mountedRef.current && !controller.signal.aborted) {
              setIntentCollapsed(true);
            }
          }, 2200);

          controller.signal.addEventListener('abort', () => clearTimeout(collapseTimer), { once: true });
        } else {
          setIntentEnvelope(result.envelope ?? null);
          setIntentStatus('error');
        }
      })
      .catch(() => {
        if (!mountedRef.current || controller.signal.aborted) {
          return;
        }

        setIntentStatus('error');
      });

    return () => {
      controller.abort();
    };
  }, [
    commandIntelligenceConfig,
    entityRef,
    hasContext,
    mode,
    intentContextSnapshot,
    messages.length,
    assistantProfile,
  ]);

  const suggestionsToShow = useMemo(() => {
    const featured =
      commandIntelligenceConfig.getFeaturedSuggestions?.({
        intl,
        hasContext,
        attachedCount,
        mode,
      }) ?? [];
    const dedupeText = new Set(featured.map(f => f.promptText.toLowerCase()));

    const fromIntent = intentEnvelope?.suggestions ?? [];

    if (fromIntent.length > 0) {
      const dynamic = fromIntent
        .map((s, i) => {
          const promptText = typeof s === 'string' ? s.trim() : '';

          if (!promptText) {
            return null;
          }

          if (dedupeText.has(promptText.toLowerCase())) {
            return null;
          }

          const maxLabel = 90;
          const label = promptText.length > maxLabel ? `${promptText.slice(0, maxLabel - 1)}…` : promptText;

          return { key: `intent-${i}-${promptText.slice(0, 24)}`, label, promptText };
        })
        .filter(Boolean);

      return [...featured, ...dynamic];
    }

    const presetItems = (preset.suggestionItems ?? [])
      .map(item => ({
        key: item.promptId,
        label: intl.formatMessage({ id: item.summaryId }),
        promptText: intl.formatMessage({ id: item.promptId }),
      }))
      .filter(item => !dedupeText.has(item.promptText.toLowerCase()));

    return [...featured, ...presetItems];
  }, [commandIntelligenceConfig, intentEnvelope, preset.suggestionItems, intl, hasContext, attachedCount, mode]);

  const sessionCreditWarnings = useMemo(() => getLatestCreditLimitWarnings(messages), [messages]);

  const latestAssistantUsage = useMemo(() => getLatestAssistantUsage(messages), [messages]);

  const userPromptCount = useMemo(() => countUserPrompts(messages), [messages]);

  const sendBlocked = useMemo(() => {
    if (messages.length === 0) {
      return false;
    }

    return isSessionPromptLimitReached(messages) || isSessionContextLimitReached(messages);
  }, [messages]);

  const promptMeterPct = useMemo(
    () => Math.min(100, Math.round((userPromptCount / COMMAND_INTELLIGENCE_MAX_USER_PROMPTS) * 100)),
    [userPromptCount]
  );

  const contextMeterPct = useMemo(() => {
    if (!latestAssistantUsage || typeof latestAssistantUsage.inputTokens !== 'number') {
      return 0;
    }

    return Math.min(
      100,
      Math.round((latestAssistantUsage.inputTokens / COMMAND_INTELLIGENCE_MAX_INPUT_CONTEXT_TOKENS) * 100)
    );
  }, [latestAssistantUsage]);

  const sessionMeterPct = Math.max(promptMeterPct, contextMeterPct);
  const sessionMeterColor = useMemo(() => {
    if (sessionMeterPct >= 92) {
      return theme.palette.warning.main;
    }

    if (sessionMeterPct >= 78) {
      return alpha(theme.palette.warning.main, 0.85);
    }

    return alpha(theme.palette.text.primary, 0.55);
  }, [sessionMeterPct, theme]);

  const promptUsageTooltip = useMemo(
    () =>
      intl.formatMessage(
        { id: 'command_intelligence_tooltip_prompts' },
        {
          count: userPromptCount,
          max: COMMAND_INTELLIGENCE_MAX_USER_PROMPTS,
          pct: promptMeterPct,
        }
      ),
    [intl, userPromptCount, promptMeterPct]
  );

  const contextUsageTooltip = useMemo(
    () =>
      latestAssistantUsage && typeof latestAssistantUsage.inputTokens === 'number'
        ? intl.formatMessage(
            { id: 'command_intelligence_tooltip_context' },
            {
              used: latestAssistantUsage.inputTokens.toLocaleString(),
              max: COMMAND_INTELLIGENCE_MAX_INPUT_CONTEXT_TOKENS.toLocaleString(),
              pct: contextMeterPct,
            }
          )
        : intl.formatMessage({ id: 'command_intelligence_tooltip_context_idle' }),
    [intl, latestAssistantUsage, contextMeterPct]
  );

  const resetThreadKeepContext = useCallback(() => {
    setMessages([]);
    channelRefRef.current = null;
    streamingIdRef.current = null;
    setIsSending(false);
    setComposerText('');
    setIntentStatus('idle');
    setIntentEnvelope(null);
    setIntentCollapsed(false);
    abortRef.current?.abort();
    abortRef.current = null;
    intentAbortRef.current?.abort();
    intentAbortRef.current = null;
  }, [abortRef, streamingIdRef]);

  const handleSendMessage = useCallback(
    userPrompt => {
      const trimmed = typeof userPrompt === 'string' ? userPrompt.trim() : '';

      if (!trimmed || isSending) {
        return;
      }

      if (messages.length > 0 && channelRefRef.current) {
        if (isSessionPromptLimitReached(messages) || isSessionContextLimitReached(messages)) {
          return;
        }
      }

      setComposerText('');

      if (messages.length === 0 || !channelRefRef.current) {
        if (!entityRef || !hasContext) {
          return;
        }

        sendFirstMessage(trimmed);
      } else {
        if (!entityRef || !hasContext) {
          return;
        }

        sendFollowUpMessage(trimmed);
      }
    },
    [entityRef, hasContext, isSending, messages, sendFirstMessage, sendFollowUpMessage]
  );

  const handleSubmitFeedback = useCallback(
    async payload => {
      const hasMessageRef = Boolean(payload?.messageRef);
      const hasRunMetadata = Boolean(payload?.runRef && (payload?.agentRef || preset.agentRef));

      if (!entityRef || !payload?.chatChannelRef || !payload?.rating || (!hasMessageRef && !hasRunMetadata)) {
        throw new Error('Missing required feedback parameters');
      }

      const enrichedPayload = {
        ...payload,
        ...(payload.agentRef ? {} : preset.agentRef ? { agentRef: preset.agentRef } : {}),
      };

      await submitFeedback(entityRef, enrichedPayload);

      setMessages(previousMessages =>
        previousMessages.map(message => {
          const metadata = message.metadata && typeof message.metadata === 'object' ? message.metadata : {};
          const messageRefMatches = message.crn === enrichedPayload.messageRef || metadata.messageRef === enrichedPayload.messageRef;
          const runMetadataMatches =
            enrichedPayload.messageRef === '' &&
            enrichedPayload.runRef &&
            enrichedPayload.agentRef &&
            metadata.runRef === enrichedPayload.runRef &&
            metadata.agentRef === enrichedPayload.agentRef;

          if (!messageRefMatches && !runMetadataMatches) {
            return message;
          }

          return {
            ...message,
            metadata: {
              ...metadata,
              feedback: {
                rating: enrichedPayload.rating,
                ...(enrichedPayload.userFeedback !== null &&
                  enrichedPayload.userFeedback !== undefined &&
                  enrichedPayload.userFeedback !== '' && { userFeedback: enrichedPayload.userFeedback }),
                timestamp: enrichedPayload.timestamp ?? Date.now(),
              },
            },
          };
        })
      );
    },
    [entityRef, preset.agentRef]
  );

  const handleComposerSubmit = useCallback(
    submittedText => {
      const text = (submittedText ?? composerText).trim();

      if (!text) {
        return;
      }

      if (sendBlocked) {
        return;
      }

      handleSendMessage(text);
    },
    [composerText, handleSendMessage, sendBlocked]
  );

  const handleCompareAgents = useCallback(async () => {
    const runCompareAgents = commandIntelligenceConfig.runCompareAgents;
    const trimmed = composerText.trim();

    if (!runCompareAgents || !trimmed || !entityRef || !hasContext || isSending || sendBlocked) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();

    abortRef.current = controller;
    setComposerText('');
    setIsSending(true);

    try {
      await runCompareAgents({
        trimmed,
        entityRef,
        mode,
        attachedItems,
        listFilters,
        session,
        assistantProfile,
        userMessage: wrapUserMessage(trimmed),
        attachedContext: buildAttachedContextPayload(),
        signal: controller.signal,
        channelRefRef,
        setMessages,
        setComposerText,
        makeId,
        mountedRef,
        intl,
        getPreset: commandIntelligenceConfig.getPreset,
      });
    } finally {
      if (mountedRef.current) {
        setIsSending(false);
      }
    }
  }, [
    commandIntelligenceConfig,
    assistantProfile,
    attachedItems,
    composerText,
    entityRef,
    hasContext,
    intl,
    isSending,
    listFilters,
    mode,
    sendBlocked,
    session,
    wrapUserMessage,
    buildAttachedContextPayload,
    abortRef,
  ]);

  const showWelcomeHero = messages.length === 0;
  const showIntentCard = hasContext && showWelcomeHero && intentStatus !== 'idle';
  const isAccessDenied = aiAccessDeniedMessage !== null;
  const showOverflowMenu = !isAccessDenied && (dockPickerEnabled || layoutModePickerEnabled || messages.length > 0);

  return (
    <Box sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={styles.headerTitleRow}>
          <AutoAwesome fontSize="small" sx={styles.headerIcon} />
          <Typography component="h2" variant="subtitle1" sx={styles.headerTitle} noWrap>
            {intl.formatMessage({ id: drawerTitleId })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={styles.headerActions}>
          {!isAccessDenied && entityRef ? (
            <CommandIntelligenceCreditsBar usageSummary={aiCreditsSummary} status={aiCreditsStatus} variant="chip" />
          ) : null}
          {!isAccessDenied ? (
            <Tooltip
              title={`${promptUsageTooltip} \u2014 ${contextUsageTooltip}`}
              arrow
              placement="bottom"
              enterDelay={150}
              disableInteractive={false}
              PopperProps={{
                sx: { zIndex: t => t.zIndex.tooltip + 1 },
                modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
              }}
              componentsProps={{
                tooltip: {
                  sx: styles.sessionMeterTooltip,
                },
              }}
            >
              <Box component="span" tabIndex={0} role="status" sx={styles.sessionMeter(sessionMeterColor)}>
                {`${sessionMeterPct}%`}
              </Box>
            </Tooltip>
          ) : null}
          {showOverflowMenu ? (
            <Tooltip title={intl.formatMessage({ id: 'command_intelligence_drawer_more_options' })} arrow>
              <IconButton
                aria-label={intl.formatMessage({ id: 'command_intelligence_drawer_more_options' })}
                size="small"
                onClick={event => setOverflowAnchorEl(event.currentTarget)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <IconButton
            aria-label={intl.formatMessage({ id: 'command_intelligence_close_aria' })}
            onClick={onClose}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {isAccessDenied ? (
        <Box sx={[styles.messagesViewport, styles.messagesViewportWelcome]}>
          <CommandIntelligenceAccessDenied message={aiAccessDeniedMessage} />
        </Box>
      ) : (
        <>
          <Menu
            anchorEl={overflowAnchorEl}
            open={overflowOpen}
            onClose={() => setOverflowAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 240,
                maxHeight: 'min(480px, calc(100vh - 24px))',
              },
            }}
            MenuListProps={{
              sx: {
                py: 0.5,
                overflowY: 'auto',
              },
            }}
          >
            {layoutModePickerEnabled ? (
              <>
                <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600, fontSize: '0.75rem' }}>
                  {intl.formatMessage({ id: 'command_intelligence_panel_layout' })}
                </MenuItem>
                {DRAWER_LAYOUT_MODES.map(mode => {
                  const Icon = LAYOUT_MODE_ICONS[mode];
                  const isSelected = layoutMode === mode;

                  return (
                    <MenuItem
                      key={mode}
                      selected={isSelected}
                      onClick={() => {
                        setOverflowAnchorEl(null);
                        onLayoutModeChange?.(mode);
                      }}
                    >
                      <ListItemIcon>{Icon ? <Icon fontSize="small" /> : null}</ListItemIcon>
                      <ListItemText primary={intl.formatMessage({ id: LAYOUT_MODE_LABEL_IDS[mode] })} />
                      {isSelected ? <Check fontSize="small" sx={{ ml: 1, opacity: 0.7 }} /> : null}
                    </MenuItem>
                  );
                })}
                {layoutMode === 'overlap' ? (
                  <MenuItem
                    selected={autoHideOnClickOutside}
                    onClick={() => {
                      setOverflowAnchorEl(null);
                      onAutoHideOnClickOutsideChange?.(!autoHideOnClickOutside);
                    }}
                  >
                    <ListItemIcon>
                      <TouchApp fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={intl.formatMessage({ id: 'command_intelligence_panel_auto_hide_overlap' })}
                    />
                    {autoHideOnClickOutside ? <Check fontSize="small" sx={{ ml: 1, opacity: 0.7 }} /> : null}
                  </MenuItem>
                ) : null}
                <Divider />
              </>
            ) : null}
            {dockPickerEnabled ? (
              <>
                <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600, fontSize: '0.75rem' }}>
                  {intl.formatMessage({ id: 'command_intelligence_dock_position' })}
                </MenuItem>
                {DOCK_PICKER_ANCHORS.map(anchor => {
                  const Icon = DOCK_ANCHOR_ICONS[anchor];
                  const isSelected = dockAnchor === anchor;

                  return (
                    <MenuItem
                      key={anchor}
                      selected={isSelected}
                      onClick={() => {
                        setOverflowAnchorEl(null);
                        onDockAnchorChange?.(anchor);
                      }}
                    >
                      <ListItemIcon>{Icon ? <Icon fontSize="small" /> : null}</ListItemIcon>
                      <ListItemText primary={intl.formatMessage({ id: DOCK_ANCHOR_LABEL_IDS[anchor] })} />
                      {isSelected ? <Check fontSize="small" sx={{ ml: 1, opacity: 0.7 }} /> : null}
                    </MenuItem>
                  );
                })}
                <Divider />
              </>
            ) : null}
            {messages.length > 0 ? (
              <MenuItem
                disabled={
                  !commandIntelligenceConfig.runCompareAgents ||
                  !composerText.trim() ||
                  !hasContext ||
                  isSending ||
                  sendBlocked
                }
                onClick={() => {
                  setOverflowAnchorEl(null);
                  void handleCompareAgents();
                }}
              >
                <ListItemIcon>
                  <CompareArrows fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: 'command_intelligence_compare_aria' })}
                  secondary={intl.formatMessage({ id: 'command_intelligence_compare_tooltip' })}
                  secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
                />
              </MenuItem>
            ) : null}
            {messages.length > 0 ? (
              <MenuItem
                onClick={() => {
                  setOverflowAnchorEl(null);
                  resetThreadKeepContext();
                }}
              >
                <ListItemIcon>
                  <RestartAlt fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={intl.formatMessage({ id: 'command_intelligence_session_reset' })} />
              </MenuItem>
            ) : null}
          </Menu>

          {profiles.length > 1 ? (
            <Box sx={styles.profileBar}>
              {profiles.map(profile => (
                <Button
                  key={profile.id}
                  type="button"
                  size="small"
                  variant={assistantProfile === profile.id ? 'contained' : 'outlined'}
                  onClick={() => setAssistantProfile(profile.id)}
                  sx={assistantProfile === profile.id ? styles.profileButtonActive : styles.profileButton}
                >
                  {intl.formatMessage({ id: profile.labelStringId })}
                </Button>
              ))}
            </Box>
          ) : null}

          {typeof renderContextBar === 'function' ? (
            <Box sx={styles.contextBar}>
              {renderContextBar({
                attachedItems,
                availableItems,
                onAttachItems,
                onDetachItem,
                onClearAttached,
                disabled: isSending,
              })}
            </Box>
          ) : null}

          <Box
            sx={[styles.messagesViewport, showWelcomeHero ? styles.messagesViewportWelcome : null]}
            data-chat-messages-viewport="true"
          >
            {showWelcomeHero ? (
              <CommandIntelligenceWelcomeHero
                title={intl.formatMessage({ id: preset.headlineId })}
                subtitle={welcomeSubheadId ? intl.formatMessage({ id: welcomeSubheadId }) : null}
                promptsLabel={intl.formatMessage({ id: 'command_intelligence_try_one_of_these' })}
                suggestions={suggestionsToShow}
                onSelectSuggestion={handleSendMessage}
                isSuggestionDisabled={isSending || !hasContext || sendBlocked}
                intentSlot={
                  showIntentCard && intentStatus === 'loading' ? (
                    <IntentProgressCard
                      status={intentStatus}
                      envelope={intentEnvelope}
                      attachedCount={attachedCount}
                      collapsed={intentCollapsed}
                      onToggleCollapsed={() => setIntentCollapsed(previous => !previous)}
                      toolCatalog={commandIntelligenceConfig.getIntentToolCatalog?.(assistantProfile) ?? []}
                      getScopeLabel={commandIntelligenceConfig.getIntentScopeLabel}
                    />
                  ) : null
                }
                isHorizontalDock={isHorizontalDock}
              />
            ) : (
              <MessageList
                messages={messages}
                isStreaming={isSending}
                onSendMessage={handleSendMessage}
                onSubmitFeedback={handleSubmitFeedback}
                entityRef={entityRef}
                chatChannelRef={channelRefRef.current || ''}
                showCopyButton={!isMobile}
                markdownDensity="compact"
                sendDisabled={sendBlocked}
              />
            )}
          </Box>

          {messages.length > 0 && (sendBlocked || (sessionCreditWarnings && sessionCreditWarnings.length > 0)) ? (
            <Box sx={styles.alertsBar}>
              {sendBlocked ? (
                <Alert severity="warning" sx={styles.alertCompact}>
                  {intl.formatMessage({
                    id: isSessionContextLimitReached(messages)
                      ? 'command_intelligence_session_limit_context'
                      : 'command_intelligence_session_limit_prompt',
                  })}
                </Alert>
              ) : null}
              {sessionCreditWarnings && sessionCreditWarnings.length > 0 ? (
                <Alert severity="warning" sx={{ ...styles.alertCompact, mt: sendBlocked ? 1 : 0 }}>
                  {sessionCreditWarnings.map((w, i) => (
                    <span key={`${w.scope}-${i}`}>
                      {i > 0 ? '; ' : ''}
                      {intl.formatMessage(
                        { id: 'command_intelligence_credit_warning_line' },
                        { pct: w.percentUsed, scope: w.scope }
                      )}
                    </span>
                  ))}
                </Alert>
              ) : null}
            </Box>
          ) : null}

          <Box sx={styles.composerForm}>
            <CommandIntelligencePromptInput
              value={composerText}
              onChange={setComposerText}
              onSubmit={handleComposerSubmit}
              onStop={handleStopGeneration}
              placeholder={
                hasContext
                  ? intl.formatMessage({ id: 'command_intelligence_placeholder_ready' })
                  : intl.formatMessage({ id: 'command_intelligence_placeholder_attach' })
              }
              disabled={!hasContext || sendBlocked}
              isLoading={isSending}
              canStop={isSending}
              showKeyboardHint={hasContext && !sendBlocked && !isHorizontalDock}
              compact={isHorizontalDock}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default CommandIntelligenceDrawerContent;
